/**
 * @fileoverview Vuex store module for Email Marketing feature.
 */

import {
  generateEmailTemplate,
  uploadImage as uploadImageApi,
  sendCampaign,
  getCampaignStats,
  getCampaigns,
  deleteCampaign,
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  getEmailLists,
  createEmailList,
  updateEmailList,
  deleteEmailList,
  getUnsubscribeList,
} from "@/api/email_marketing";

const state = {
  templates: [],
  emailLists: [],
  campaigns: [],
  unsubscribeList: [],
  loading: false,
  // Template builder state
  uploadedImages: [],
  currentTemplate: null,       // { html, subject }
  generatingTemplate: false,
  // Campaign sending state
  sendingCampaign: false,
  sendProgress: null,          // { sent, failed, total }
};

const mutations = {
  setTemplates(state, templates) {
    state.templates = templates;
  },
  addTemplate(state, template) {
    state.templates.push(template);
  },
  updateTemplate(state, { id, data }) {
    const idx = state.templates.findIndex(t => (t._id || t.id) === id);
    if (idx !== -1) Object.assign(state.templates[idx], data);
  },
  removeTemplate(state, id) {
    state.templates = state.templates.filter(t => (t._id || t.id) !== id);
  },
  setEmailLists(state, lists) {
    state.emailLists = lists;
  },
  addEmailList(state, list) {
    state.emailLists.push(list);
  },
  updateEmailList(state, { id, data }) {
    const idx = state.emailLists.findIndex(l => (l._id || l.id) === id);
    if (idx !== -1) Object.assign(state.emailLists[idx], data);
  },
  removeEmailList(state, id) {
    state.emailLists = state.emailLists.filter(l => (l._id || l.id) !== id);
  },
  setLoading(state, val) {
    state.loading = val;
  },
  addUploadedImage(state, image) {
    state.uploadedImages.push(image);
  },
  removeUploadedImage(state, index) {
    const img = state.uploadedImages[index];
    if (img && img.url) URL.revokeObjectURL(img.url);
    state.uploadedImages.splice(index, 1);
  },
  clearUploadedImages(state) {
    state.uploadedImages.forEach(img => {
      if (img && img.url) URL.revokeObjectURL(img.url);
    });
    state.uploadedImages = [];
  },
  setCurrentTemplate(state, template) {
    state.currentTemplate = template;
  },
  setGeneratingTemplate(state, val) {
    state.generatingTemplate = val;
  },
  setCampaigns(state, campaigns) {
    state.campaigns = campaigns;
  },
  addCampaign(state, campaign) {
    state.campaigns.push(campaign);
  },
  removeCampaign(state, id) {
    state.campaigns = state.campaigns.filter(c => (c._id || c.id) !== id);
  },
  setUnsubscribeList(state, list) {
    state.unsubscribeList = list;
  },
  setSendingCampaign(state, val) {
    state.sendingCampaign = val;
  },
  setSendProgress(state, progress) {
    state.sendProgress = progress;
  },
};

const getters = {
  getTemplates: state => state.templates,
  getEmailLists: state => state.emailLists,
  getCampaigns: state => state.campaigns,
  isLoading: state => state.loading,
  getCurrentTemplate: state => state.currentTemplate,
  getUploadedImages: state => state.uploadedImages,
  getTotalRecipients: state => {
    return state.emailLists.reduce((sum, list) => sum + (list.recipients ? list.recipients.length : 0), 0);
  },
};

const actions = {
  // ---- Templates ----
  async loadTemplates({ commit }) {
    commit('setLoading', true);
    try {
      const templates = await getTemplates();
      commit('setTemplates', Array.isArray(templates) ? templates : []);
    } catch (e) {
      console.error('Failed to load templates:', e);
    } finally {
      commit('setLoading', false);
    }
  },

  async saveTemplate({ commit }, { name, html, subject, images }) {
    const template = await createTemplate({ name, html, subject, images, createdAt: new Date().toISOString() });
    commit('addTemplate', template);
    return template;
  },

  async editTemplate({ commit }, { id, data }) {
    const updated = await updateTemplate(id, data);
    commit('updateTemplate', { id, data: updated });
    return updated;
  },

  async removeTemplate({ commit }, id) {
    await deleteTemplate(id);
    commit('removeTemplate', id);
  },

  // ---- Email Lists ----
  async loadEmailLists({ commit }) {
    commit('setLoading', true);
    try {
      const lists = await getEmailLists();
      commit('setEmailLists', Array.isArray(lists) ? lists : []);
    } catch (e) {
      console.error('Failed to load email lists:', e);
    } finally {
      commit('setLoading', false);
    }
  },

  async loadCampaigns({ commit }) {
    try {
      const campaigns = await getCampaigns();
      commit('setCampaigns', Array.isArray(campaigns) ? campaigns : []);
    } catch (e) {
      console.error('Failed to load campaigns:', e);
    }
  },

  async saveEmailList({ commit }, { name, recipients }) {
    const list = await createEmailList({ name, recipients });
    commit('addEmailList', list);
    return list;
  },

  async editEmailList({ commit }, { id, data }) {
    const updated = await updateEmailList(id, data);
    commit('updateEmailList', { id, data: updated });
    return updated;
  },

  async removeEmailList({ commit }, id) {
    await deleteEmailList(id);
    commit('removeEmailList', id);
  },

  // ---- Image Upload ----
  async uploadImage({ commit }, file) {
    let previewUrl = null;
    try {
      previewUrl = URL.createObjectURL(file);
      const formData = new FormData();
      formData.append('file', file);
      const result = await uploadImageApi(formData);
      const uploaded = Array.isArray(result) ? result[0] : result;
      const serverFilename = uploaded.uploaded || uploaded.fileName || file.name;
      commit('addUploadedImage', { url: previewUrl, filename: serverFilename });
      return serverFilename;
    } catch (err) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      console.error('Image upload error:', err);
    }
  },

  // ---- Template Generation ----
  async generateTemplate({ commit, state }, { businessInfo, templateType, ctaUrl }) {
    commit('setGeneratingTemplate', true);
    try {
      const images = state.uploadedImages.map(img => img.filename);
      const result = await generateEmailTemplate(images, businessInfo, templateType, ctaUrl);
      commit('setCurrentTemplate', { html: result.html, subject: result.subject || '' });
      return result;
    } finally {
      commit('setGeneratingTemplate', false);
    }
  },

  // ---- Campaign ----
  async launchCampaign({ commit }, params) {
    commit('setSendingCampaign', true);
    var recipientCount = params.recipients ? params.recipients.length : 0;
    commit('setSendProgress', { sent: 0, failed: 0, total: recipientCount });
    try {
      const result = await sendCampaign(params);
      const campaign = {
        id: result.campaignId,
        templateId: params.templateId,
        subject: params.subject,
        senderName: params.senderName,
        senderEmail: params.senderEmail,
        recipientCount: result.total || recipientCount,
        sent: result.sent,
        failed: result.failed,
        openRate: 0,
        clickRate: 0,
        status: 'SENDING',
        createdAt: new Date().toISOString(),
      };
      commit('addCampaign', campaign);
      commit('setSendProgress', { sent: result.sent, failed: result.failed, total: result.total });
      return campaign;
    } finally {
      commit('setSendingCampaign', false);
    }
  },

  async fetchCampaignStats(_, campaignId) {
    return await getCampaignStats(campaignId);
  },

  async removeCampaign({ commit }, id) {
    await deleteCampaign(id);
    commit('removeCampaign', id);
  },

  async loadUnsubscribeList({ commit }) {
    try {
      const list = await getUnsubscribeList();
      commit('setUnsubscribeList', Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Failed to load unsubscribe list:', e);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
